import { localStorage } from './storage';

export interface MfeStorageEntry {
  url: string;
  expiresAt?: string;
}

const overrideKey = (windowName: string): string => `${windowName}_mfe_override`;

const isValidHttpUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname);
  } catch {
    return false;
  }
};

const readOverrideEntry = (windowName: string): MfeStorageEntry | null => {
  const raw = localStorage.getItem(overrideKey(windowName));
  if (raw === null) return null;
  try {
    const json = JSON.parse(raw);
    if (!json || typeof json !== 'object' || typeof json.url !== 'string') {
      localStorage.removeItem(overrideKey(windowName));
      return null;
    }
    if (!isValidHttpUrl(json.url)) {
      localStorage.removeItem(overrideKey(windowName));
      return null;
    }
    if (typeof json.expiresAt === 'string') {
      const d = new Date(json.expiresAt);
      if (isNaN(d.getTime()) || d <= new Date()) {
        localStorage.removeItem(overrideKey(windowName));
        return null;
      }
    }
    return typeof json.expiresAt === 'string'
      ? { url: json.url, expiresAt: json.expiresAt }
      : { url: json.url };
  } catch {
    localStorage.removeItem(overrideKey(windowName));
    return null;
  }
};

const readPlainUrlEntry = (windowName: string): MfeStorageEntry | null => {
  const raw = localStorage.getItem(windowName);
  if (raw === null) return null;
  if (raw.trimStart().startsWith('{')) {
    console.warn(
      `[mfeStorage] localStorage["${windowName}"] contains JSON from an abandoned storage format ` +
        `(introduced in PR #5749, superseded by the _mfe_override key). ` +
        `Clear this entry manually or it will continue to be ignored.`
    );
    localStorage.removeItem(windowName);
    return null;
  }
  if (!isValidHttpUrl(raw)) {
    localStorage.removeItem(windowName);
    return null;
  }
  return { url: raw };
};

export const readMfeStorageEntry = (windowName: string): MfeStorageEntry | null => {
  return readOverrideEntry(windowName) ?? readPlainUrlEntry(windowName);
};

export const writeMfeStorageEntry = (windowName: string, url: string, expiresAt?: string): void => {
  if (!isValidHttpUrl(url)) return;
  const entry: MfeStorageEntry = expiresAt ? { url, expiresAt } : { url };
  localStorage.setItem(overrideKey(windowName), JSON.stringify(entry));
};

export const removeMfeStorageEntry = (windowName: string): void => {
  localStorage.removeItem(overrideKey(windowName));
  // Also clear the plain-URL key written by WDIO tests and external tooling so the toolbar
  // "remove" action clears all override sources for this MFE.
  localStorage.removeItem(windowName);
};

export const getEndOfDay = (daysFromNow = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};
